import { useEffect, useState, useCallback, useRef } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { taskAPI, activityAPI, activityEventsAPI, dashboardCardAPI, projectAPI, noteAPI, notificationAPI, dashboardAPI, digestAPI, clientGroupAPI, integrationAPI, leaveAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { useLiveInvalidate } from '../lib/useLiveData';
import LastSyncedPill from '../components/LastSyncedPill';
import { WidgetInfo, WidgetFooter, widgetMeta } from '../components/DashboardWidgets';
import WidgetGalleryV2 from '../components/WidgetGalleryV2';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { UserChip } from '../components/UserChip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  Plus, ArrowUpRight, Calendar, Timer, ChevronRight, Circle, CheckCircle2, Check,
  AlertTriangle, Clock, Brain, TrendingUp, X, Sparkles, ClipboardList, PieChart, Users,
  BarChart, Calculator, StickyNote, FolderKanban, MessageSquare, Activity,
  Gauge, List, Settings2, GripVertical, Maximize2, Minimize2, Building2, Plug, Bell, RefreshCw, Download, RotateCcw, Flag
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { OnboardingTour, useOnboarding } from '../components/OnboardingTour';
import { formatTaskDate, formatDate } from '../lib/dateFormat';
import { getTodayLocal, formatLocalDateHeader, getLocalDateString } from '../utils/date';
import { TaskStatusIndicator } from '../components/TaskStatusIndicator';
import TaskTitleWithClient from '../components/TaskTitleWithClient';
import ToContinueBanner from '../components/attendance/ToContinueBanner';
import { TaskDetailSheet } from '../components/TaskDetailSheet';
import { Responsive as ResponsiveGrid } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';

const PRIORITY_DOT = { high: 'bg-red-500/70', medium: 'bg-gold/70', low: 'bg-foreground/20' };
const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', waiting: 'Waiting', review: 'Review', completed: 'Completed', cancelled: 'Cancelled' };
const ICON_MAP = { Gauge, ClipboardList, AlertTriangle, Clock, Activity, List, PieChart, Users, BarChart, Timer, Calculator, StickyNote, CheckCircle2, FolderKanban, Calendar, Sparkles, MessageSquare, Building2, Plug, Bell };
const SIZE_MAP = { small: { w: 1, h: 2 }, medium: { w: 1, h: 3 }, large: { w: 2, h: 3 } };

function getGreeting() { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; }

export default function TaskDashboardPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [cards, setCards] = useState([]);
  const [activities, setActivities] = useState([]);
  // Notifications v2 (2026-06-04) — important-only feed for the dashboard widget.
  const [importantNotifications, setImportantNotifications] = useState([]);
  // FEATURE 6 (2026-06-04) — Activity feed filter state. Owner/Admin/Manager
  // can filter the visible feed by person or by role; the backend enforces
  // hierarchy so a filter cannot expose anyone outside the viewer's scope.
  const [activityVisibleUsers, setActivityVisibleUsers] = useState([]);
  const [activityFilterUserId, setActivityFilterUserId] = useState('');
  const [activityFilterRole, setActivityFilterRole] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // D84 — Live sync indicator. Updates whenever the dashboard refetches
  // via WS push, polling, or manual click on the pill itself.
  const [lastSynced, setLastSynced] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardTemplates, setCardTemplates] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [layouts, setLayouts] = useState({});
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  // Batch 5 R2 F4 — when this flag is ON, the full-screen Widget Gallery v2
  // replaces the legacy slide-over add-card dialog.
  const useGalleryV2 = !!currentWorkspace?.feature_flags?.flag_widget_gallery_v2;
  const [notes, setNotes] = useState([]);
  const [notifications, setNotifications] = useState([]);
  // Batch 2 D-014 (BUG-RW-D-014) — persist Dismiss state to localStorage
  // scoped to today (YYYY-MM-DD). Resets daily so important notices come
  // back the next day. Server-side cross-device sync deferred; localStorage
  // alone solves the "banner reappears after refresh" complaint.
  const dismissKey = `stellar_dashboard_dismissed_${getLocalDateString(getTodayLocal())}`;
  const [dismissedNotifs, setDismissedNotifs] = useState(() => {
    try {
      const stored = localStorage.getItem(dismissKey);
      if (stored) return new Set(JSON.parse(stored));
    } catch {}
    return new Set();
  });
  // Persist Set changes to localStorage on every update.
  useEffect(() => {
    try { localStorage.setItem(dismissKey, JSON.stringify(Array.from(dismissedNotifs))); } catch {}
  }, [dismissedNotifs, dismissKey]);
  // 2026-02-20 — "Mark this day as planned" chip. Collapses the three
  // top alert banners (overdue / follow-ups / due-soon) into a single
  // one-line summary. Auto-resets daily via the date-scoped key so
  // tomorrow's alerts come back automatically. localStorage only —
  // no server sync needed (per-device "I've seen it" preference).
  const dayPlannedKey = `stellar_dashboard_day_planned_${getLocalDateString(getTodayLocal())}`;
  const [dayPlanned, setDayPlanned] = useState(() => {
    try { return localStorage.getItem(dayPlannedKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try {
      if (dayPlanned) localStorage.setItem(dayPlannedKey, '1');
      else localStorage.removeItem(dayPlannedKey);
    } catch {}
  }, [dayPlanned, dayPlannedKey]);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfDays, setPdfDays] = useState(30);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const gridRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);
  const navigate = useNavigate();
  // Phase 2 Group A — must reflect the user's LOCAL wall clock, not UTC.
  // Works for Saskatchewan (UTC-6) and India (UTC+5:30) alike.
  const today = getTodayLocal();
  const { showTour, setShowTour } = useOnboarding();
  const role = user?.role || '';
  const isAdmin = ['owner', 'admin', 'manager', 'super_admin'].includes(role);

  const fetchAll = useCallback((silent = false) => {
    if (!currentWorkspace?.id) return;
    if (!silent) setLoading(true);
    Promise.all([
      taskAPI.dashboard(currentWorkspace.id).catch(() => ({ data: {} })),
      taskAPI.workload(currentWorkspace.id).catch(() => ({ data: {} })),
      dashboardCardAPI.list(currentWorkspace.id).catch(() => ({ data: [] })),
      // BUG C (2026-06-04) — Activity refetch MUST use the current filter
      // state. Earlier the `useCallback` deps array omitted the filter
      // values, so the live-invalidate refetch (firing every WS event)
      // captured the initial empty-filter closure and overwrote the
      // filtered list with the unfiltered one. Filter values are now in
      // the deps so fetchAll is rebuilt whenever the filter changes.
      activityAPI
        .list(currentWorkspace.id, 20, { userId: activityFilterUserId, role: activityFilterRole })
        .catch(() => ({ data: [] })),
      projectAPI.list(currentWorkspace.id).catch(() => ({ data: [] })),
      noteAPI.list(currentWorkspace.id).catch(() => ({ data: [] })),
      notificationAPI.list(20).catch(() => ({ data: { items: [] } })),
      notificationAPI.listImportant(10).catch(() => ({ data: { items: [] } })),
    ]).then(([dR, wR, cR, aR, pR, nR, notifR, impR]) => {
      setDashboard(dR.data); setWorkload(wR.data);
      setActivities(aR.data || []); setProjects(pR.data || []); setNotes(nR.data || []);
      setNotifications(notifR.data?.items || []);
      setImportantNotifications(impR.data?.items || []);
      // Only rebuild cards & layout if cards actually changed (prevents layout reset on silent refresh)
      const newCards = cR.data || [];
      setCards(prev => {
        const prevIds = prev.map(c => c.id).join(',');
        const newIds = newCards.map(c => c.id).join(',');
        if (prevIds === newIds && prev.length > 0) return prev; // Same cards, keep existing
        return newCards;
      });
      // Build layout only on initial load or when cards change
      setLayouts(prev => {
        if (prev.lg && prev.lg.length > 0 && prev.lg.length === newCards.length) return prev; // Keep existing layout
        // Iter 58: detect persisted "compaction corruption" — when a previous
        // browser session at a sub-lg breakpoint stomped the canonical lg
        // layout into a single-column stack (every card x=0, w=1). When that
        // is detected, ignore the broken DB coords and re-derive the 2-col
        // spec layout in the frontend so the dashboard renders correctly.
        // The next onLayoutChange at lg will repersist the correct shape.
        const persistedLooksOk = newCards.some((c) =>
          c.layout && (c.layout.w >= 2 || c.layout.x >= 2)
        );
        const lg = newCards.map((c, i) => {
          const s = SIZE_MAP[c.size] || SIZE_MAP.medium;
          if (persistedLooksOk && c.layout && typeof c.layout.x === 'number') {
            return { i: c.id, x: c.layout.x, y: c.layout.y, w: c.layout.w || s.w, h: c.layout.h || s.h, minW: 1, minH: 1, maxW: 4 };
          }
          // Re-derive 2-col grid from card index (ignores corrupted DB coords)
          const w = 2;
          const col = (i % 2) * 2;
          const row = Math.floor(i / 2);
          return { i: c.id, x: col, y: row * 3, w, h: s.h, minW: 1, minH: 1, maxW: 4 };
        });
        return { lg };
      });
    }).catch(console.error).finally(() => { setLoading(false); setLastSynced(new Date()); });
  }, [currentWorkspace?.id, activityFilterUserId, activityFilterRole]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // FEATURE 6 — refetch the activity stream when the filter changes.
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    activityAPI
      .list(currentWorkspace.id, 20, { userId: activityFilterUserId, role: activityFilterRole })
      .then((aR) => setActivities(aR.data || []))
      .catch(() => {});
  }, [currentWorkspace?.id, activityFilterUserId, activityFilterRole]);
  // FEATURE 6 — load the set of users this viewer is allowed to filter by.
  useEffect(() => {
    if (!currentWorkspace?.id || !isAdmin) return;
    activityEventsAPI
      .visibleUsers(currentWorkspace.id)
      .then((r) => setActivityVisibleUsers(r.data || []))
      .catch(() => setActivityVisibleUsers([]));
  }, [currentWorkspace?.id, isAdmin]);
  // BUG-07 — refresh on global task-changed event + 15s poll fallback.
  // D88+++ (2026-06-04): both the event listener AND the setInterval
  // now refetch in SILENT mode — no skeleton flicker, no top-level
  // setLoading(true) flip that re-mounts dashboard widgets. The 5-minute
  // auto-refresh below was already silent.
  useEffect(() => {
    const onChanged = () => fetchAll(true);
    window.addEventListener('stellar-task-changed', onChanged);
    const pollId = setInterval(onChanged, 15_000);
    return () => {
      window.removeEventListener('stellar-task-changed', onChanged);
      clearInterval(pollId);
    };
  }, [fetchAll]);
  // D84 — Real-time push: any teammate change to tasks, clients, EOD,
  // pipeline, or reviews invalidates dashboard cards. Silent refetch.
  const _dashRefetch = useCallback(() => { setLastSynced(new Date()); fetchAll(true); }, [fetchAll]);
  useLiveInvalidate('tasks', _dashRefetch);
  useLiveInvalidate('clients', _dashRefetch);
  useLiveInvalidate('pipeline', _dashRefetch);
  useLiveInvalidate('eod', _dashRefetch);
  useLiveInvalidate('reviews', _dashRefetch);
  // Auto-refresh every 5 minutes — SILENT (no skeleton)
  useEffect(() => {
    const interval = setInterval(() => fetchAll(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAll]);
  useEffect(() => { dashboardCardAPI.templates().then(r => setCardTemplates(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!gridRef.current) return;
    setGridWidth(gridRef.current.offsetWidth || 1200);
    const ro = new ResizeObserver(entries => { for (const e of entries) setGridWidth(e.contentRect.width); });
    ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, [loading, cards.length]);

  const addCard = async (template) => {
    if (cards.some(c => c.card_type === template.id)) { toast.error('Already on dashboard'); return; }
    try {
      const r = await dashboardCardAPI.add(currentWorkspace.id, { card_type: template.id, title: template.name, size: template.default_size || 'medium', position: cards.length });
      toast.success(`Added "${template.name}"`);
      // BUG-D08 — optimistic insert so the new card is rendered immediately
      // even if fetchAll's identity-equality short-circuit defers a refresh.
      const newCard = r?.data || { id: r?.data?.id, card_type: template.id, title: template.name, size: template.default_size || 'medium' };
      setCards(prev => prev.some(c => c.id === newCard.id) ? prev : [...prev, newCard]);
      setLayouts(prev => {
        const lg = prev.lg || [];
        if (lg.some(l => l.i === newCard.id)) return prev;
        const s = SIZE_MAP[newCard.size] || SIZE_MAP.medium;
        const idx = lg.length;
        const col = (idx % 2) * 2;
        const row = Math.floor(idx / 2);
        return { ...prev, lg: [...lg, { i: newCard.id, x: col, y: row * 3, w: 2, h: s.h, minW: 1, minH: 1, maxW: 4 }] };
      });
      fetchAll(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const removeCard = async (cardId) => {
    try {
      await dashboardCardAPI.remove(currentWorkspace.id, cardId);
      setCards(prev => prev.filter(c => c.id !== cardId));
      setLayouts(prev => ({ lg: (prev.lg || []).filter(l => l.i !== cardId) }));
    } catch {}
  };

  const onLayoutChange = (layout, allLayouts) => {
    // Iter 58: ResponsiveGrid auto-compacts cards to 1-col / w=1 when the
    // viewport is below lg (1024px). If we naïvely persist `layout` in that
    // state, we permanently corrupt the canonical desktop layout in the DB
    // (the bug that caused all 8 cards to stack on the left after Iter 55).
    // Two guards:
    //   (a) keep the full `allLayouts` dict in state so re-mounts at any
    //       breakpoint render correctly without a refetch.
    //   (b) only PERSIST when the user is currently viewing the lg
    //       breakpoint — that's the canonical desktop layout we save to disk.
    setLayouts(prev => allLayouts ? { ...prev, ...allLayouts } : { lg: layout });
    if (currentBreakpoint !== 'lg') return;
    const updatedCards = cards.map(c => {
      const l = layout.find(li => li.i === c.id);
      return l ? { ...c, layout: { i: l.i, x: l.x, y: l.y, w: l.w, h: l.h } } : c;
    });
    dashboardCardAPI.reorder(currentWorkspace.id, updatedCards).catch(() => {});
  };

  const downloadPdf = async () => {
    if (!currentWorkspace?.id) return;
    setPdfLoading(true);
    try {
      const res = await dashboardAPI.downloadPdf(currentWorkspace.id, pdfDays);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stellar_report_${pdfDays}d_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      setShowPdfDialog(false);
      toast.success('Report downloaded');
    } catch { toast.error('Failed to generate report'); }
    setPdfLoading(false);
  };

  const sendDailyDigest = async () => {
    // 2026-02-22 — Replaced "Send AI Daily Digest" with "Send Report to
    // Team" per user directive. Uses the currently-selected time-range
    // (`pdfDays`, snapped to 7/14/30/60/90) and the new tracked endpoint
    // POST /api/digest/send-team-report/{ws} which records each delivery
    // for the owner Digest Log and embeds an open-tracking pixel.
    if (!currentWorkspace?.id) return;
    const allowed = [7, 14, 30, 60, 90];
    const days = allowed.includes(pdfDays) ? pdfDays : 7;
    setDigestLoading(true);
    try {
      const res = await digestAPI.sendTeamReport(currentWorkspace.id, days);
      const sent = res.data?.sent_count ?? 0;
      const failed = res.data?.failed_count ?? 0;
      if (sent > 0 && failed === 0) {
        toast.success(`Digest sent to ${sent} team member${sent === 1 ? '' : 's'}. You'll be notified as they open it.`);
      } else if (sent > 0 && failed > 0) {
        toast.warning(`Sent to ${sent}, ${failed} failed. Check the Digest Log for details.`);
      } else {
        toast.error(`No digests sent (${failed} failed). Check the Digest Log for details.`);
      }
      setShowPdfDialog(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send digest');
    }
    setDigestLoading(false);
  };

  // Per-card refresh — updates only the data that card needs
  const [refreshingCard, setRefreshingCard] = useState(null);
  const refreshCard = async (cardType) => {
    if (!currentWorkspace?.id) return;
    setRefreshingCard(cardType);
    // A11 D-015 — keep the refresh skeleton on-screen for at least 350ms even
    // when the API responds faster, so the animate-pulse visual ack is
    // actually visible to users (and to E2E tests).
    const minDisplayMs = 350;
    const startedAt = Date.now();
    try {
      if (['capacity', 'overdue', 'due_soon', 'upcoming_followups', 'workload_status', 'workload_assignee', 'priority_breakdown', 'calculation'].includes(cardType)) {
        const [dR, wR] = await Promise.all([
          taskAPI.dashboard(currentWorkspace.id).catch(() => ({ data: {} })),
          taskAPI.workload(currentWorkspace.id).catch(() => ({ data: {} })),
        ]);
        setDashboard(dR.data); setWorkload(wR.data);
      } else if (['my_tasks', 'recent_completed', 'upcoming_deadlines'].includes(cardType)) {
        const dR = await taskAPI.dashboard(currentWorkspace.id).catch(() => ({ data: {} }));
        setDashboard(dR.data);
      } else if (cardType === 'activity' || cardType === 'discussion') {
        const aR = await activityAPI
          .list(currentWorkspace.id, 20, { userId: activityFilterUserId, role: activityFilterRole })
          .catch(() => ({ data: [] }));
        setActivities(aR.data || []);
      } else if (cardType === 'project_progress') {
        const pR = await projectAPI.list(currentWorkspace.id).catch(() => ({ data: [] }));
        setProjects(pR.data || []);
      } else if (cardType === 'notes') {
        const nR = await noteAPI.list(currentWorkspace.id).catch(() => ({ data: [] }));
        setNotes(nR.data || []);
      } else {
        // Fallback: refresh dashboard + workload
        const [dR, wR] = await Promise.all([
          taskAPI.dashboard(currentWorkspace.id).catch(() => ({ data: {} })),
          taskAPI.workload(currentWorkspace.id).catch(() => ({ data: {} })),
        ]);
        setDashboard(dR.data); setWorkload(wR.data);
      }
    } catch {}
    const elapsed = Date.now() - startedAt;
    if (elapsed < minDisplayMs) {
      await new Promise(r => setTimeout(r, minDisplayMs - elapsed));
    }
    setRefreshingCard(null);
  };

  if (loading) return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
      <Skeleton className="h-10 w-80" /><Skeleton className="h-6 w-48" />
      <div className="grid grid-cols-2 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
    </div>
  );

  const d = dashboard || {};
  const w = workload || {};
  const workloadNotifs = w.notifications || [];
  const capacityPct = Math.min(100, Math.round(((w.active_load || 0) / (w.baseline_capacity || 8)) * 100));

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-4" data-testid="task-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-[13px] text-muted-foreground flex items-center gap-2" data-testid="dashboard-header-date">
            <span>{formatLocalDateHeader(today)}</span>
          </p>
          <h1 className="text-xl md:text-2xl font-display font-bold tracking-tight mt-0.5">{getGreeting()}, {user?.name?.split(' ')[0] || 'there'}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!dayPlanned && ((w.overdue_tasks || []).length + (w.upcoming_followups || []).length + (w.due_soon_tasks || []).length > 0) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDayPlanned(true)}
              className="text-[11px] text-muted-foreground/70 hover:text-emerald-400 gap-1.5"
              data-testid="day-planned-chip"
              title="Collapse the top alert banners into a one-line summary for today"
            >
              <Check className="h-3 w-3" /> Mark day as planned
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowPdfDialog(true)} data-testid="download-report-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" /> <span className="hidden sm:inline">Report</span>
          </Button>
          <Button size="sm" onClick={() => navigate('/tasks/all?new=true')} data-testid="dashboard-new-task-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> <span className="hidden sm:inline">New Task</span>
          </Button>
          {/* D84 — Real-time sync indicator. Green when the workspace WS is
              live; amber/grey when falling back to polling or disconnected.
              Click to force a refetch + a WS reconnect attempt. */}
          <LastSyncedPill lastSynced={lastSynced} loading={loading} onRefetch={() => fetchAll(true)} />
        </div>
      </div>

      {/* 2026-06-06 — Resume Paused Tasks banner. Renders only when the
          user just clocked in AND their previous clock-out auto-paused
          one or more in_progress tasks. Self-hides after dismiss /
          full resume. Theme-aware. */}
      <ToContinueBanner />

      {/* 2026-02-20 — "Mark this day as planned" — collapsed summary.
          Shows a single one-line aggregate when the user has marked
          the day as planned. Auto-resets daily via the date-scoped
          localStorage key. */}
      {dayPlanned && ((w.overdue_tasks || []).length + (w.upcoming_followups || []).length + (w.due_soon_tasks || []).length > 0) && (
        <div
          className="flex items-center gap-3 p-2.5 rounded-lg border border-border/30 bg-foreground/[0.02]"
          data-testid="day-planned-summary"
        >
          <Check className="h-4 w-4 shrink-0 text-emerald-400/70" />
          <p className="text-[12px] leading-relaxed flex-1 text-muted-foreground/80">
            Day marked as planned ·
            {(w.overdue_tasks || []).length > 0 && <span className="text-red-400/80"> {(w.overdue_tasks || []).length} overdue</span>}
            {(w.overdue_tasks || []).length > 0 && ((w.upcoming_followups || []).length > 0 || (w.due_soon_tasks || []).length > 0) && <span> ·</span>}
            {(w.upcoming_followups || []).length > 0 && <span className="text-amber-400/80"> {(w.upcoming_followups || []).length} follow-up{(w.upcoming_followups || []).length > 1 ? 's' : ''}</span>}
            {(w.upcoming_followups || []).length > 0 && (w.due_soon_tasks || []).length > 0 && <span> ·</span>}
            {(w.due_soon_tasks || []).length > 0 && <span className="text-gold/80"> {(w.due_soon_tasks || []).length} due soon</span>}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDayPlanned(false)}
            className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground shrink-0"
            data-testid="day-planned-expand-btn"
          >
            Expand
          </Button>
        </div>
      )}

      {/* Overdue Alert — always first, always red */}
      {!dayPlanned && (w.overdue_tasks || []).length > 0 && !dismissedNotifs.has('overdue_main') && (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-red-500/20 bg-red-500/[0.04]" data-testid="overdue-alert">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-500/60" />
          <p className="text-[13px] leading-relaxed flex-1">
            You have <strong className="text-red-500/80">{(w.overdue_tasks || []).length} overdue task{(w.overdue_tasks || []).length > 1 ? 's' : ''}</strong>. {(w.overdue_tasks || [])[0]?.client_name ? <><span className="font-semibold text-foreground/90">{(w.overdue_tasks || [])[0].client_name}</span> — </> : null}"{(w.overdue_tasks || [])[0]?.title}" needs immediate attention.
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            {(w.overdue_tasks || [])[0]?.id && (
              <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 border-red-500/20 text-red-500/70 hover:text-red-500" onClick={() => setSelectedTaskId((w.overdue_tasks || [])[0]?.id)}>View</Button>
            )}
            <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-muted-foreground" onClick={() => setDismissedNotifs(prev => new Set([...prev, 'overdue_main']))}>Dismiss</Button>
          </div>
        </div>
      )}

      {/* 2026-02-20 — Upcoming Follow-ups alert banner. Stacked DIRECTLY
          below the overdue banner per user spec. Same styling pattern,
          amber theme (between red=overdue and gold=due-soon). Renders
          one row per follow-up firing today/tomorrow; max 3 visible
          with a "+N more" link to the Deadline Calendar. */}
      {!dayPlanned && (w.upcoming_followups || []).length > 0 && !dismissedNotifs.has('upcoming_followups_main') && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3 space-y-1.5" data-testid="upcoming-followups-alert">
          <div className="flex items-start gap-3">
            <Flag className="h-4 w-4 mt-0.5 shrink-0 text-amber-400/70" />
            <p className="text-[13px] leading-relaxed flex-1 text-amber-100/90">
              You have <strong className="text-amber-400/90">{(w.upcoming_followups || []).length} follow-up{(w.upcoming_followups || []).length > 1 ? 's' : ''}</strong> landing {(w.upcoming_followups || []).some(r => r.is_today) ? 'today' : 'tomorrow'}.
            </p>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-muted-foreground shrink-0" onClick={() => setDismissedNotifs(prev => new Set([...prev, 'upcoming_followups_main']))}>Dismiss</Button>
          </div>
          <div className="pl-7 space-y-1">
            {(w.upcoming_followups || []).slice(0, 3).map(r => (
              <button
                key={`${r.task_id}-fu-${r.follow_up_index}`}
                onClick={() => setSelectedTaskId(r.task_id)}
                className="flex items-center gap-2 w-full text-left text-[12px] hover:bg-amber-500/[0.06] rounded px-1.5 py-0.5 transition-colors"
                data-testid={`upcoming-followup-alert-${r.task_id}-${r.follow_up_index}`}
              >
                <span className={`text-[10px] uppercase tracking-wider font-semibold shrink-0 ${r.is_today ? 'text-amber-400/90' : 'text-amber-400/60'}`}>
                  FU{r.follow_up_index} · {r.is_today ? 'Today' : 'Tomorrow'}
                </span>
                <span className="text-amber-100/85 truncate">
                  {r.client_name ? <><span className="font-semibold">{r.client_name}</span> — </> : null}{r.task_title}{r.follow_up_label ? ` — ${r.follow_up_label}` : ''}
                </span>
              </button>
            ))}
            {(w.upcoming_followups || []).length > 3 && (
              <button
                onClick={() => navigate('/calendar')}
                className="text-[11px] text-amber-400/60 hover:text-amber-400 pl-1.5"
              >
                +{(w.upcoming_followups || []).length - 3} more — open Deadline Calendar →
              </button>
            )}
          </div>
        </div>
      )}

      {/* 2026-02-20 — Due Soon alert banner. Stacked under the
          follow-ups banner. Gold accent (mirrors the Due Soon widget
          icon). Surfaces the same 3-day rolling window the widget
          shows but as a persistent, non-dismissible-while-active row
          at the top so users see it without scrolling. */}
      {!dayPlanned && (w.due_soon_tasks || []).length > 0 && !dismissedNotifs.has('due_soon_main') && (
        <div className="rounded-lg border border-gold/25 bg-gold/[0.04] p-3 space-y-1.5" data-testid="due-soon-alert">
          <div className="flex items-start gap-3">
            <Clock className="h-4 w-4 mt-0.5 shrink-0 text-gold/70" />
            <p className="text-[13px] leading-relaxed flex-1 text-foreground/90">
              You have <strong className="text-gold/90">{(w.due_soon_tasks || []).length} task{(w.due_soon_tasks || []).length > 1 ? 's' : ''}</strong> due in the next 2 days.
            </p>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-muted-foreground shrink-0" onClick={() => setDismissedNotifs(prev => new Set([...prev, 'due_soon_main']))}>Dismiss</Button>
          </div>
          <div className="pl-7 space-y-1">
            {(w.due_soon_tasks || []).slice(0, 3).map(t => (
              <button
                key={t.id}
                onClick={() => setSelectedTaskId(t.id)}
                className="flex items-center gap-2 w-full text-left text-[12px] hover:bg-gold/[0.06] rounded px-1.5 py-0.5 transition-colors"
                data-testid={`due-soon-alert-${t.id}`}
              >
                <span className="text-[10px] uppercase tracking-wider font-semibold shrink-0 text-gold/70">
                  {t.due_date ? formatTaskDate(t.due_date) : 'Soon'}
                </span>
                <span className="text-foreground/85 truncate">{t.client_name ? <><span className="font-semibold">{t.client_name}</span> — </> : null}{t.title}</span>
              </button>
            ))}
            {(w.due_soon_tasks || []).length > 3 && (
              <button
                onClick={() => navigate('/tasks/all?filter=due_soon')}
                className="text-[11px] text-gold/60 hover:text-gold pl-1.5"
              >
                +{(w.due_soon_tasks || []).length - 3} more — open task list →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Other Smart Notifications */}
      {workloadNotifs.length > 0 && (
        <div className="space-y-1.5">
          {workloadNotifs.filter(n => n.type !== 'overdue' && !dismissedNotifs.has(n.message)).slice(0, 2).map((n, i) => {
            const Icon = n.type === 'progress' ? TrendingUp : Brain;
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border/50 bg-card/50">
                <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground/50" />
                <p className="text-[13px] leading-relaxed flex-1">{n.message}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  {n.task_id && <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => setSelectedTaskId(n.task_id)}>View</Button>}
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-muted-foreground" onClick={() => setDismissedNotifs(prev => new Set([...prev, n.message]))}>Dismiss</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 2026-07-31 — FIXED dashboard layout (owner directive): the
          drag/resize customization system is removed. Everyone sees the
          same 7 cards in a simple responsive grid. Card components and
          their data stay untouched — only the layout system changed. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fixed-dashboard-grid">
        {[
          { id: 'fixed-my_tasks', card_type: 'my_tasks', title: 'My Tasks' },
          { id: 'fixed-due_soon', card_type: 'due_soon', title: 'Due Soon' },
          { id: 'fixed-overdue', card_type: 'overdue', title: 'Overdue Tasks' },
          { id: 'fixed-activity', card_type: 'activity', title: 'Activity Feed' },
          // 2026-07 — Workload by Assignee + Team Report cards removed (owner directive).
          { id: 'fixed-calendar_preview', card_type: 'calendar_preview', title: 'Calendar Preview' },
        ].map(card => (
          <div key={card.id} data-testid={`card-${card.card_type}`} className="min-h-[300px] max-h-[420px]">
            <Card className="h-full bg-card/60 backdrop-blur card-hover-gold group overflow-hidden">
              <CardContent className="p-3 md:p-4 h-full flex flex-col">
                <div className="flex items-center justify-between mb-2 shrink-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-wider truncate">{card.title}</span>
                    <WidgetInfo cardType={card.card_type} />
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); refreshCard(card.card_type); }}
                    className="p-0.5 rounded hover:bg-foreground/[0.05] opacity-30 hover:opacity-100 group-hover:opacity-100 transition-opacity"
                    title="Refresh this card"
                    aria-label="Refresh this card"
                    data-testid={`card-refresh-${card.card_type}`}
                  >
                    <RefreshCw className={`h-3 w-3 text-muted-foreground/50 hover:text-gold ${refreshingCard === card.card_type ? 'animate-spin text-gold' : ''}`} />
                  </button>
                </div>
                <div className="flex-1 card-scroll-area relative overflow-y-auto" style={{ maxHeight: 'calc(100% - 28px)' }}>
                  <CardRenderer card={card} d={d} w={w} activities={activities} projects={projects} notes={notes} importantNotifications={importantNotifications} onTaskClick={setSelectedTaskId} isAdmin={isAdmin} navigate={navigate} workspaceId={currentWorkspace?.id} user={user} activityVisibleUsers={activityVisibleUsers} activityFilterUserId={activityFilterUserId} setActivityFilterUserId={setActivityFilterUserId} activityFilterRole={activityFilterRole} setActivityFilterRole={setActivityFilterRole} />
                  {refreshingCard === card.card_type && (
                    <div
                      className="absolute inset-0 bg-background/70 backdrop-blur-[1px] flex flex-col gap-2 p-2 pointer-events-none animate-in fade-in-0 duration-150"
                      data-testid={`card-refresh-skeleton-${card.card_type}`}
                    >
                      <div className="h-3 w-1/2 rounded bg-foreground/[0.08] animate-pulse" />
                      <div className="h-3 w-3/4 rounded bg-foreground/[0.08] animate-pulse" />
                      <div className="h-3 w-2/3 rounded bg-foreground/[0.08] animate-pulse" />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* 2026-07-31 — Widget gallery / "Your work-life balance" customization
          removed with the fixed dashboard layout. */}

      {/* PDF Report Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="sm:max-w-[380px]" data-testid="pdf-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Download Report</DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground/50">Generate a PDF summary of your dashboard data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-[11px] text-muted-foreground/40 font-medium uppercase tracking-wider">Time range</p>
            <div className="grid grid-cols-3 gap-2">
              {[{ label: '7 days', val: 7 }, { label: '14 days', val: 14 }, { label: '30 days', val: 30 }, { label: '60 days', val: 60 }, { label: '90 days', val: 90 }, { label: 'All time', val: 3650 }].map(opt => (
                <button
                  key={opt.val}
                  onClick={() => setPdfDays(opt.val)}
                  className={`py-2 rounded-lg text-[12px] border transition-colors ${pdfDays === opt.val ? 'border-gold/40 bg-gold/5 text-gold' : 'border-border/20 text-muted-foreground hover:border-border/40'}`}
                  data-testid={`pdf-range-${opt.val}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button className="w-full h-9 bg-gold/10 hover:bg-gold/20 text-gold border-0 text-[12px]" onClick={downloadPdf} disabled={pdfLoading} data-testid="pdf-download-btn">
              {pdfLoading ? <><RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> Generating...</> : <><Download className="h-3.5 w-3.5 mr-2" /> Download PDF</>}
            </Button>
            {isAdmin && (
              <>
                <div className="relative my-2"><div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border/20" /></div><div className="relative flex justify-center"><span className="bg-card px-2 text-[10px] text-muted-foreground/30">or</span></div></div>
                <Button variant="outline" className="w-full h-9 text-[12px] border-border/20" onClick={sendDailyDigest} disabled={digestLoading || ![7,14,30,60,90].includes(pdfDays)} data-testid="send-digest-btn" title={![7,14,30,60,90].includes(pdfDays) ? 'Pick a range between 7 and 90 days to send to the team' : ''}>
                  {digestLoading ? <><RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> Sending...</> : <><Bell className="h-3.5 w-3.5 mr-2" /> Send Report to Team</>}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TaskDetailSheet taskId={selectedTaskId} open={!!selectedTaskId} onOpenChange={o => { if (!o) setSelectedTaskId(null); }} onUpdate={fetchAll} />
      {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}
    </div>
  );
}

// 2026-07 — Calendar Preview: real mini month-grid with due-date markers.
function MiniCalendarPreview({ tasks, onTaskClick }) {
  const [selectedDay, setSelectedDay] = useState(null);
  const now = new Date();
  const year = now.getFullYear(), month = now.getMonth();
  const startDow = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDay = {};
  (tasks || []).forEach(t => {
    const ds = (t.due_date || '').slice(0, 10);
    if (!ds) return;
    const [y, m, day] = ds.split('-').map(Number);
    if (y === year && m === month + 1) (byDay[day] = byDay[day] || []).push(t);
  });
  const today = now.getDate();
  const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  return (
    <div className="flex flex-col h-full" data-testid="mini-calendar-preview">
      <p className="text-[11px] font-semibold text-foreground/70 mb-1.5">{now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((dl) => (
          <span key={dl} className="text-[9px] text-muted-foreground/40 font-semibold py-0.5">{dl}</span>
        ))}
        {cells.map((day, i) => day === null ? <span key={`e${i}`} /> : (
          <button
            key={day}
            onClick={() => setSelectedDay(selectedDay === day ? null : day)}
            data-testid={`mini-cal-day-${day}`}
            className={`relative h-7 rounded-md text-[10px] flex items-center justify-center transition-colors ${selectedDay === day ? 'bg-gold/25 text-gold font-bold' : day === today ? 'bg-foreground/[0.08] text-foreground font-bold' : 'text-muted-foreground/70 hover:bg-foreground/[0.04]'}`}
          >
            {day}
            {(byDay[day] || []).length > 0 && (
              <span className="flex gap-0.5 absolute bottom-0.5 left-1/2 -translate-x-1/2">
                {(byDay[day] || []).slice(0, 3).map((t, j) => <span key={j} className="h-1 w-1 rounded-full bg-gold" />)}
              </span>
            )}
          </button>
        ))}
      </div>
      {selectedDay !== null && (
        <div className="mt-1.5 border-t border-border/20 pt-1.5 overflow-y-auto max-h-28" data-testid="mini-cal-day-tasks">
          {(byDay[selectedDay] || []).length === 0
            ? <p className="text-[10px] text-muted-foreground/40 px-1">Nothing due on {now.toLocaleDateString('en-US', { month: 'short' })} {selectedDay}</p>
            : (byDay[selectedDay] || []).map(t => (
              <button key={t.id} onClick={() => onTaskClick(t.id)} className="flex items-center gap-2 py-1 px-1.5 rounded task-row-hover text-[11px] w-full text-left" data-testid={`mini-cal-task-${t.id}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                <span className="truncate flex-1">{t.title}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function CardRenderer({ card, d, w, activities, projects, notes, importantNotifications = [], onTaskClick, isAdmin, navigate, workspaceId, user, activityVisibleUsers = [], activityFilterUserId = '', setActivityFilterUserId = () => {}, activityFilterRole = '', setActivityFilterRole = () => {} }) {
  const capacityPct = Math.min(100, Math.round(((w.active_load || 0) / (w.baseline_capacity || 8)) * 100));
  const meta = widgetMeta(card.card_type);
  const goViewAll = () => meta.viewAllPath && navigate(meta.viewAllPath);
  switch (card.card_type) {
    case 'capacity': {
      // D94 / 2026-02-25 — Attendance/Clock UI is DELETED. Show a generic
      // placeholder when there's no work tracked instead of asking the
      // user to "clock in" (which no longer exists). Capacity progress
      // still works once tasks accrue tracked time.
      const noActiveSession = (!w.active_load || w.active_load === 0) && (!w.done_hours || w.done_hours === 0);
      if (noActiveSession) {
        return (
          <div>
            <button onClick={goViewAll} className="w-full text-left hover:opacity-80 transition-opacity" data-testid="capacity-open">
              <div className="text-[14px] font-medium text-foreground/80 mt-2 mb-1">No tracked work yet</div>
              <p className="text-[11px] text-muted-foreground/60 leading-relaxed">Start a task to track time. Your daily capacity will populate as tasks accrue hours.</p>
            </button>
            <WidgetFooter cardType={card.card_type} onClick={goViewAll} label="Plan my day →" />
          </div>
        );
      }
      return (
        <div>
          <button onClick={goViewAll} className="w-full text-left hover:opacity-80 transition-opacity" data-testid="capacity-open">
            <div className="flex items-end gap-2 mb-2"><span className="text-2xl font-display font-bold">{w.active_load || 0}h</span><span className="text-[13px] text-muted-foreground mb-0.5">/ {w.baseline_capacity || 8}h</span></div>
            <Progress value={capacityPct} className="h-1.5 mb-1.5" />
            <div className="flex justify-between text-[10px] text-muted-foreground"><span>{w.done_hours || 0}h done</span><span>{w.remaining_capacity || 0}h left</span></div>
          </button>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} label="Plan my day →" />
        </div>
      );
    }
    case 'my_tasks':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto">
            {(d.my_tasks||[]).slice(0,8).map(t=>(
              <button key={t.id} onClick={()=>onTaskClick(t.id)} className="flex items-center gap-2 w-full py-1 px-1.5 rounded task-row-hover text-left" data-testid={`my-task-${t.id}`}>
                <Circle className="h-3 w-3 text-foreground/15 shrink-0"/>
                <span className="text-[12px] truncate flex-1">
                  <TaskTitleWithClient
                    clientName={t.client_name}
                    title={t.title}
                    suffix={t.due_date ? formatTaskDate(t.due_date) : null}
                    testId={`my-task-client-${t.id}`}
                  />
                </span>
                {t.status !== 'completed' && t.status !== 'cancelled' && <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[t.priority]}`}/>}
                <TaskStatusIndicator status={t.status} size="xs" />
              </button>
            ))}
            {(d.my_tasks||[]).length===0 && <EmptyState label="You have no assigned tasks" cta="Create one" onCta={()=>navigate('/tasks/all?new=true')} />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'overdue':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto">
            {(w.overdue_tasks||[]).slice(0,6).map(t=>(
              <button key={t.id} onClick={()=>onTaskClick(t.id)} className="flex items-center gap-2 w-full py-1 px-1.5 rounded task-row-hover text-left" data-testid={`overdue-task-${t.id}`}>
                <AlertTriangle className="h-3 w-3 text-red-500/50 shrink-0"/>
                <span className="text-[12px] truncate flex-1">
                  <TaskTitleWithClient
                    clientName={t.client_name}
                    title={t.title}
                    suffix={t.due_date ? formatTaskDate(t.due_date) : null}
                    testId={`overdue-client-${t.id}`}
                  />
                </span>
              </button>
            ))}
            {(w.overdue_tasks||[]).length===0 && <EmptyState label="Nothing overdue — nice work" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'due_soon':
      // 2026-02-20 — Request 1 fix: Due Soon now reads from
      // `w.due_soon_tasks` (the 3-day rolling window). Fallback to the
      // legacy `upcoming_tasks` keeps the widget functional during the
      // brief rollout window where caches/snapshots may not have the
      // new field yet.
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto">
            {((w.due_soon_tasks && w.due_soon_tasks.length > 0) ? w.due_soon_tasks : (w.upcoming_tasks||[])).slice(0,6).map(t=>(
              <button key={t.id} onClick={()=>onTaskClick(t.id)} className="flex items-center gap-2 w-full py-1 px-1.5 rounded task-row-hover text-left" data-testid={`due-soon-${t.id}`}>
                <Clock className="h-3 w-3 text-muted-foreground/30 shrink-0"/>
                <span className="text-[12px] truncate flex-1">
                  <TaskTitleWithClient
                    clientName={t.client_name}
                    title={t.title}
                    suffix={t.due_date ? formatTaskDate(t.due_date) : null}
                    testId={`due-soon-client-${t.id}`}
                  />
                </span>
              </button>
            ))}
            {((w.due_soon_tasks && w.due_soon_tasks.length > 0) ? w.due_soon_tasks : (w.upcoming_tasks||[])).length===0 && <EmptyState label="No tasks due in the next 2 days" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'upcoming_followups':
      // 2026-02-20 — Deprecated as a widget. Follow-ups now render as a
      // fixed alert banner at the top of the dashboard (under the
      // overdue banner) — see the top section of this page. The widget
      // case is retained as a graceful empty render so any existing
      // saved layout that still references it doesn't crash; users can
      // remove it via Customize.
      return (
        <div className="flex flex-col h-full items-center justify-center text-center px-3">
          <p className="text-[11px] text-muted-foreground/60">
            Upcoming follow-ups now appear as an alert banner at the top of the dashboard. You can remove this widget from Customize.
          </p>
        </div>
      );
    case 'activity':
      if(!isAdmin) return <p className="text-[12px] text-muted-foreground/40 text-center py-4">Admin only</p>;
      return (
        <div className="flex flex-col h-full">
          {/* FEATURE 6 (2026-06-04) — Filter controls visible to Owner /
              Admin / Manager. Backend enforces hierarchy so a Manager only
              sees their direct reports here.
              BUG C (2026-06-04) — `select-none` + onMouseDown stopPropagation
              on the bar so clicking a dropdown doesn't initiate a
              text-selection drag (the buttons rendering each activity row
              below are textful, so React's default click→drag→select on
              text was leaking past the dropdown and "following" the cursor).
              react-grid-layout's drag is already gated on `.drag-handle`,
              but stopping the mouse event here also prevents any
              ancestor from picking up the click. */}
          <div
            className="flex items-center gap-1.5 mb-1.5 select-none"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            data-testid="activity-filter-bar"
          >
            <select
              value={activityFilterUserId}
              onChange={(e) => setActivityFilterUserId(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="h-6 text-[10px] bg-card border border-border/30 rounded px-1.5 max-w-[110px] truncate focus:outline-none focus:border-gold/40 cursor-pointer"
              data-testid="activity-filter-person"
            >
              <option value="">All people</option>
              <option value={user?.id || ''}>{(user?.name || 'Me') + ' (me)'}</option>
              {activityVisibleUsers.map((u) => (
                <option key={u.user_id} value={u.user_id}>{u.name}</option>
              ))}
            </select>
            <select
              value={activityFilterRole}
              onChange={(e) => setActivityFilterRole(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="h-6 text-[10px] bg-card border border-border/30 rounded px-1.5 focus:outline-none focus:border-gold/40 cursor-pointer"
              data-testid="activity-filter-role"
            >
              <option value="">All roles</option>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="team">Team</option>
            </select>
            {(activityFilterUserId || activityFilterRole) && (
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setActivityFilterUserId('');
                  setActivityFilterRole('');
                }}
                className="text-[10px] text-muted-foreground/70 hover:text-foreground"
                data-testid="activity-filter-clear"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex-1 space-y-0.5 overflow-auto">
            {activities.slice(0,10).map(a=>(
              <button
                key={a.id}
                type="button"
                onClick={()=> a.task_id ? onTaskClick(a.task_id) : navigate(`/tasks/activity/${a.user_id}`)}
                className="flex items-start gap-2 py-1 px-1.5 rounded task-row-hover text-[11px] w-full text-left select-none"
                data-testid={`activity-${a.id}`}
              >
                <Activity className="h-3 w-3 text-muted-foreground/30 mt-0.5 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{a.user_name}</span>
                  <span className="text-muted-foreground"> {a.action}</span>
                  {a.task_title && <span className="text-muted-foreground/60"> — {a.task_title}</span>}
                </div>
                <span className="text-[9px] text-muted-foreground/30 shrink-0">{new Date(a.created_at).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}</span>
              </button>
            ))}
            {activities.length===0 && <EmptyState label="No activity yet today" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'workload_status': {
      // BUG-D12 — surface ALL canonical statuses (not just the ones with
      // non-zero counts). Previously the widget rendered Object.entries
      // of d.status_counts, which omits "In Progress" and "Waiting" when
      // nobody happened to be in those buckets. Now we render the full
      // ordered set so the breakdown is consistent on every dashboard.
      const STATUS_ORDER = ['not_started', 'in_progress', 'waiting', 'review', 'completed', 'cancelled'];
      const counts = d.status_counts || {};
      const hasAny = STATUS_ORDER.some(s => (counts[s] || 0) > 0);
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-1 overflow-auto">
            {hasAny ? STATUS_ORDER.map(s => (
              <button
                key={s}
                onClick={()=>navigate(`/tasks/all?tab=by-status&status=${s}`)}
                className="flex items-center justify-between py-1 px-1.5 rounded task-row-hover w-full text-left"
                data-testid={`workload-status-${s}`}
              >
                <span className="text-[12px]">{STATUS_LABELS[s]||s}</span>
                <span className={`text-[12px] font-medium tabular-nums ${(counts[s]||0) === 0 ? 'text-muted-foreground/40' : ''}`}>{counts[s] || 0}</span>
              </button>
            )) : <EmptyState label="No tasks in this workspace" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    }
    case 'workload_assignee':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-1 overflow-auto">
            {(d.team_hours||[]).map(th=>(
              <button
                key={th.user_id}
                onClick={()=>navigate(`/tasks/team-view?user=${th.user_id}`)}
                className="flex items-center justify-between py-1 px-1.5 rounded task-row-hover w-full text-left"
                data-testid={`wl-assignee-${th.user_id}`}
              >
                <span className="text-[12px] truncate">{th.name}</span>
                <span className="text-[12px] font-medium tabular-nums">{th.hours}h</span>
              </button>
            ))}
            {(d.team_hours||[]).length===0 && <EmptyState label="No team hours yet" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'priority_breakdown':
      return (
        <div>
          <div className="flex items-center gap-6 justify-center py-3">
            {['high','medium','low'].map(p=>(
              <button
                key={p}
                onClick={()=>navigate('/tasks/all')}
                className="text-center group hover:opacity-80 transition-opacity"
                data-testid={`priority-${p}`}
              >
                <p className="text-xl font-display font-bold group-hover:text-gold">{(d.priority_counts||{})[p]||0}</p>
                <p className="text-[10px] text-muted-foreground capitalize">{p}</p>
              </button>
            ))}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'calculation': {
      const total = d.total_tasks || 0;
      const completed = (d.status_counts||{}).completed || 0;
      const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
      return (
        <div>
          <button onClick={goViewAll} className="w-full hover:opacity-80 transition-opacity text-center py-2" data-testid="calc-total">
            <p className="text-3xl font-display font-bold">{total}</p>
            <p className="text-[11px] text-muted-foreground">Total Tasks</p>
            <div className="flex items-center justify-center gap-3 mt-2 pt-2 border-t border-border/20">
              <div className="text-center">
                <p className="text-[13px] font-semibold tabular-nums">{completed}</p>
                <p className="text-[9px] text-muted-foreground/60">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-[13px] font-semibold tabular-nums text-gold">{pct}%</p>
                <p className="text-[9px] text-muted-foreground/60">Done</p>
              </div>
            </div>
          </button>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    }
    case 'time_tracking':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-1 overflow-auto">
            {(d.team_hours||[]).slice(0,6).map(th=>(
              <button
                key={th.user_id}
                onClick={()=>navigate(`/tasks/activity/${th.user_id}`)}
                className="flex items-center justify-between py-1 px-1.5 rounded task-row-hover w-full text-left"
                data-testid={`time-track-${th.user_id}`}
              >
                <span className="text-[12px] truncate">{th.name}</span>
                <span className="text-[12px] font-medium tabular-nums">{th.hours}h</span>
              </button>
            ))}
            {(d.team_hours||[]).length===0 && <EmptyState label="No hours logged" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'project_progress':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-2 overflow-auto">
            {projects.slice(0,5).map(p=>{
              const pct = p.task_count>0 ? Math.round((p.completed_count/p.task_count)*100) : 0;
              return (
                <button
                  key={p.id}
                  onClick={()=>navigate(`/tasks/projects?id=${p.id}`)}
                  className="w-full text-left task-row-hover py-1 px-1.5 rounded"
                  data-testid={`project-prog-${p.id}`}
                >
                  <div className="flex items-center justify-between text-[12px] mb-1"><span className="truncate">{p.name}</span><span className="text-muted-foreground tabular-nums">{pct}%</span></div>
                  <Progress value={pct} className="h-1"/>
                </button>
              );
            })}
            {projects.length===0 && <EmptyState label="No projects yet" cta="Create project" onCta={()=>navigate('/tasks/projects')} />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'recent_completed':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto">
            {((d.recent_activity||[]).filter(t=>t.status==='completed')).slice(0,5).map(t=>(
              <button
                key={t.id}
                onClick={()=>onTaskClick(t.id)}
                className="flex items-center gap-2 py-1 px-1.5 rounded task-row-hover text-[12px] w-full text-left"
                data-testid={`completed-${t.id}`}
              >
                <CheckCircle2 className="h-3 w-3 text-foreground/20 shrink-0"/>
                <span className="truncate text-muted-foreground line-through">{t.title}</span>
              </button>
            ))}
            {((d.recent_activity||[]).filter(t=>t.status==='completed')).length===0 && <EmptyState label="Nothing shipped yet" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'upcoming_deadlines':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto">
            {(d.calendar_tasks||[]).slice(0,8).map(t=>(
              <button
                key={t.id}
                onClick={()=>onTaskClick(t.id)}
                className="flex items-center gap-2 py-1 px-1.5 rounded task-row-hover text-[12px] w-full text-left"
                data-testid={`deadline-${t.id}`}
              >
                <Calendar className="h-3 w-3 text-muted-foreground/30 shrink-0"/>
                <span className="truncate flex-1">
                  <TaskTitleWithClient
                    clientName={t.client_name}
                    title={t.title}
                    suffix={t.due_date ? formatTaskDate(t.due_date) : null}
                    testId={`deadline-client-${t.id}`}
                  />
                </span>
              </button>
            ))}
            {(d.calendar_tasks||[]).length===0 && <EmptyState label="No upcoming deadlines" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'ai_suggestions':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-1 overflow-auto">
            {(w.notifications||[]).slice(0,3).map((n,i)=>(
              <button
                key={i}
                onClick={()=> n.task_id ? onTaskClick(n.task_id) : goViewAll()}
                className="text-[12px] text-muted-foreground leading-relaxed py-1.5 px-1.5 rounded task-row-hover text-left w-full"
                data-testid={`ai-sug-${i}`}
              >
                {n.message}
              </button>
            ))}
            {(w.notifications||[]).length===0 && <EmptyState label="All clear — nothing urgent" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'notes':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto">
            {(notes||[]).slice(0,6).map(n=>(
              <button
                key={n.id}
                onClick={()=>navigate(`/tasks/notes?id=${n.id}`)}
                className="flex items-center gap-2 w-full py-1.5 px-1.5 rounded task-row-hover text-left"
                data-testid={`note-${n.id}`}
              >
                <StickyNote className="h-3 w-3 text-muted-foreground/30 shrink-0"/>
                <span className="text-[12px] truncate flex-1">{n.title||'Untitled'}</span>
                <span className="text-[10px] text-muted-foreground/40">{formatTaskDate(n.updated_at?.slice(0,10))}</span>
              </button>
            ))}
            {(notes||[]).length===0 && <EmptyState label="No notes yet" cta="Create a note" onCta={()=>navigate('/tasks/notes')} />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'discussion': {
      const recentComments = activities.filter(a => a.action?.includes('comment')).slice(0, 6);
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto">
            {recentComments.length > 0 ? recentComments.map(a=>(
              <button
                key={a.id}
                onClick={()=> a.task_id ? onTaskClick(a.task_id) : goViewAll()}
                className="flex items-start gap-2 py-1 px-1.5 rounded task-row-hover text-[11px] w-full text-left"
                data-testid={`discuss-${a.id}`}
              >
                <MessageSquare className="h-3 w-3 text-muted-foreground/30 mt-0.5 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{a.user_name}</span>
                  <span className="text-muted-foreground"> {a.action}</span>
                  {a.task_title && <span className="text-muted-foreground/60"> — {a.task_title}</span>}
                </div>
              </button>
            )) : <EmptyState label="No recent comments" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    }
    case 'team_monitor':
      if(!isAdmin) return <p className="text-[12px] text-muted-foreground/40 text-center py-4">Admin only</p>;
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-1 overflow-auto">
            {(d.team_hours||[]).slice(0,6).map(th=>(
              <button key={th.user_id} onClick={()=>navigate(`/tasks/team-view?user=${th.user_id}`)} className="flex items-center gap-2 w-full py-1.5 px-1.5 rounded task-row-hover text-left" data-testid={`team-mon-${th.user_id}`}>
                <div className="h-5 w-5 rounded-full bg-secondary flex items-center justify-center shrink-0"><span className="text-[8px] font-medium">{(th.name||'?')[0].toUpperCase()}</span></div>
                {/* Iter 65a — UserChip rendering site #2 (Team Pulse / team_monitor widget) */}
                <UserChip user={th} showRole size="sm" className="flex-1 truncate" />
                <span className="text-[10px] text-muted-foreground tabular-nums">{th.hours}h</span>
              </button>
            ))}
            {(d.team_hours||[]).length===0 && <EmptyState label="No team data" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'team_availability':
      return <TeamAvailabilityCard workspaceId={workspaceId} navigate={navigate} />;
    case 'team_report':
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-1 overflow-auto">
            {(d.team_hours||[]).slice(0,5).map(th=>(
              <button
                key={th.user_id}
                onClick={()=>navigate(`/tasks/activity/${th.user_id}`)}
                className="flex items-center justify-between py-1 px-1.5 rounded task-row-hover text-[12px] w-full text-left"
                data-testid={`team-report-${th.user_id}`}
              >
                <span className="truncate">{th.name}</span>
                <span className="font-medium tabular-nums">{th.hours}h</span>
              </button>
            ))}
            {(d.team_hours||[]).length===0 && <EmptyState label="No team data" />}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'calendar_preview':
      // 2026-07 — actual mini month-grid calendar with due-date dot markers;
      // clicking a day reveals the tasks due that day.
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-auto">
            <MiniCalendarPreview tasks={d.calendar_tasks || []} onTaskClick={onTaskClick} />
          </div>
          <WidgetFooter cardType={card.card_type} onClick={goViewAll} />
        </div>
      );
    case 'clients':
      return <ClientsCard workspaceId={workspaceId} navigate={navigate} />;
    case 'integrations':
      return <IntegrationsCard workspaceId={workspaceId} navigate={navigate} />;
    case 'ai_insight':
      return (
        <div className="flex flex-col h-full">
          <p className="text-[12px] leading-relaxed text-muted-foreground whitespace-pre-wrap line-clamp-[12] flex-1">{card.config?.text || 'No content'}</p>
          <WidgetFooter cardType={card.card_type} onClick={() => navigate('/tasks/ai-chat')} />
        </div>
      );
    case 'timesheet':
      return <TimesheetCard navigate={navigate} isAdmin={isAdmin} workspaceId={workspaceId} />;
    case 'drive_files':
      return <DriveCard navigate={navigate} workspaceId={workspaceId} />;
    case 'calendar_meetings':
      return <CalendarMeetingsCard navigate={navigate} workspaceId={workspaceId} />;
    case 'team_pulse':
      return <TeamPulseCard workspaceId={workspaceId} isAdmin={isAdmin} navigate={navigate} />;
    case 'notifications':
      // Notifications v2 (2026-06-04) — dashboard notification widget.
      // Server already filters to the current user's own notifications and
      // excludes low-value reminders, then sorts unread→priority→newest.
      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-0.5 overflow-auto" data-testid="notifications-widget">
            {importantNotifications.length === 0 && (
              <EmptyState label="No new notifications" />
            )}
            {importantNotifications.slice(0, 8).map((n) => {
              // Click should both navigate AND mark read. Falls back to
              // /tasks/all if the notification was logged before the
              // structured task_id field existed.
              const target = n.link
                || (n.task_id ? `/tasks/all?task=${n.task_id}` : '/tasks/all');
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={async () => {
                    try { await notificationAPI.markRead(n.id); } catch (_) { /* noop */ }
                    if (n.task_id) {
                      onTaskClick(n.task_id);
                    } else {
                      navigate(target);
                    }
                  }}
                  className={`flex items-start gap-2 py-1.5 px-1.5 rounded task-row-hover text-[11px] w-full text-left select-none ${
                    n.read ? 'opacity-60' : ''
                  }`}
                  data-testid={`notif-${n.id}`}
                >
                  {!n.read && (
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                  )}
                  {n.read && (
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-transparent shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`truncate ${!n.read ? 'font-medium' : ''}`}>{n.title || n.type}</div>
                    {n.message && (
                      <div className="text-[10px] text-muted-foreground/70 truncate">{n.message}</div>
                    )}
                  </div>
                  <span className="text-[9px] text-muted-foreground/40 shrink-0">
                    {n.created_at ? new Date(n.created_at).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}) : ''}
                  </span>
                </button>
              );
            })}
          </div>
          <WidgetFooter cardType={card.card_type} onClick={() => navigate('/tasks/notifications')} />
        </div>
      );
    default:
      return <p className="text-[12px] text-muted-foreground/40 text-center py-4">{card.card_type}</p>;
  }
}

function EmptyState({ label, cta, onCta }) {
  return (
    <div className="text-center py-4">
      <p className="text-[12px] text-muted-foreground/40">{label}</p>
      {cta && <Button variant="ghost" size="sm" className="text-[11px] mt-1" onClick={onCta}>{cta}</Button>}
    </div>
  );
}

function ClientsCard({ workspaceId, navigate }) {
  const [clients, setClients] = useState(null);
  useEffect(() => {
    if (!workspaceId) return;
    clientGroupAPI.list(workspaceId).then(r => setClients(sortClientsAlpha(r.data || []))).catch(() => setClients([]));
  }, [workspaceId]);
  if (clients === null) return <p className="text-[12px] text-muted-foreground/30 text-center py-4">Loading…</p>;
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-0.5 overflow-auto">
        {clients.slice(0, 6).map(c => (
          <button
            key={c.id}
            onClick={() => navigate(`/tasks/clients?id=${c.id}`)}
            className="flex items-center gap-2 w-full py-1.5 px-1.5 rounded task-row-hover text-left"
            data-testid={`client-${c.id}`}
          >
            {c.logo_url
              ? <img src={c.logo_url} alt="" className="h-5 w-5 rounded object-cover shrink-0" />
              : <div className="h-5 w-5 rounded bg-secondary flex items-center justify-center shrink-0"><Building2 className="h-3 w-3 text-muted-foreground/50" /></div>}
            <span className="text-[12px] truncate flex-1">{c.name || 'Unnamed client'}</span>
            {typeof c.task_count === 'number' && <span className="text-[10px] text-muted-foreground/50 tabular-nums">{c.task_count}</span>}
          </button>
        ))}
        {clients.length === 0 && <EmptyState label="No clients yet" cta="Add client" onCta={() => navigate('/tasks/clients')} />}
      </div>
      <WidgetFooter cardType="clients" onClick={() => navigate('/tasks/clients')} />
    </div>
  );
}

function IntegrationsCard({ workspaceId, navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!workspaceId) return;
    integrationAPI.connected(workspaceId).then(r => setData(r.data || {})).catch(() => setData({}));
  }, [workspaceId]);
  if (!data) return <p className="text-[12px] text-muted-foreground/30 text-center py-4">Loading…</p>;
  // Normalise potentially shaped-as-object or shaped-as-array response
  const entries = Array.isArray(data) ? data : Object.entries(data).map(([k, v]) => ({ platform: k, connected: !!v }));
  const known = [
    { key: 'google_drive', label: 'Google Drive' },
    { key: 'google_calendar', label: 'Google Calendar' },
    { key: 'notion', label: 'Notion' },
    { key: 'meta_pixel', label: 'Meta Pixel' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'instagram', label: 'Instagram' },
  ];
  const status = (key) => {
    const match = entries.find(e => (e.platform || e.key || '').toLowerCase().replace(/-/g, '_') === key);
    return match && (match.connected || match.status === 'connected' || match === true);
  };
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-0.5 overflow-auto">
        {known.map(k => {
          const on = !!status(k.key);
          return (
            <button
              key={k.key}
              onClick={() => navigate('/tasks/integrations')}
              className="flex items-center gap-2 w-full py-1.5 px-1.5 rounded task-row-hover text-left"
              data-testid={`integration-${k.key}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${on ? 'bg-green-500/70' : 'bg-foreground/15'}`} />
              <span className="text-[12px] truncate flex-1">{k.label}</span>
              <span className={`text-[9px] uppercase tracking-wider ${on ? 'text-green-400/70' : 'text-muted-foreground/40'}`}>{on ? 'Connected' : 'Off'}</span>
            </button>
          );
        })}
      </div>
      <WidgetFooter cardType="integrations" onClick={() => navigate('/tasks/integrations')} />
    </div>
  );
}

function TeamAvailabilityCard({ workspaceId, navigate }) {
  const [leaves, setLeaves] = useState(null);
  useEffect(() => {
    if (!workspaceId) return;
    leaveAPI.list(workspaceId, 'approved').then(r => setLeaves(r.data || [])).catch(() => setLeaves([]));
  }, [workspaceId]);
  if (leaves === null) return <p className="text-[12px] text-muted-foreground/30 text-center py-4">Loading…</p>;
  // Phase 2 Group A — local date, not UTC. Prevents a Saskatchewan user at
  // 7 PM (already May 19 UTC) from seeing yesterday's leave list.
  const today = getLocalDateString();
  const active = leaves.filter(l => {
    const s = (l.start_date || '').slice(0, 10);
    const e = (l.end_date || l.start_date || '').slice(0, 10);
    return s <= today && today <= e;
  });
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-0.5 overflow-auto">
        {active.slice(0, 6).map(l => (
          <button
            key={l.id}
            onClick={() => navigate('/tasks/availability')}
            className="flex items-center gap-2 w-full py-1.5 px-1.5 rounded task-row-hover text-left"
            data-testid={`leave-${l.id}`}
          >
            <Calendar className="h-3 w-3 text-amber-400/60 shrink-0" />
            <span className="text-[12px] truncate flex-1">{l.user_name || l.user || 'Team member'}</span>
            <span className="text-[10px] text-muted-foreground/50">
              until {formatTaskDate((l.end_date || l.start_date || '').slice(0, 10))}
            </span>
          </button>
        ))}
        {active.length === 0 && <EmptyState label="Everyone is available today" />}
      </div>
      <WidgetFooter cardType="team_availability" onClick={() => navigate('/tasks/availability')} />
    </div>
  );
}

function CalendarMeetingsCard({ workspaceId, navigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!workspaceId) return;
    dashboardCardAPI.cardData(workspaceId, 'calendar_meetings').then(r => setData(r.data)).catch(() => setData({ connected: false }));
  }, [workspaceId]);
  if (!data) return <p className="text-[12px] text-muted-foreground/30 text-center py-4">Loading…</p>;
  if (!data.connected) return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex items-center justify-center">
        <EmptyState label="Google Calendar not connected" cta="Connect now →" onCta={() => navigate('/tasks/integrations')} />
      </div>
    </div>
  );
  const events = data.events || data.meetings || [];
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-0.5 overflow-auto">
        {events.slice(0, 6).map((e, i) => (
          <a
            key={e.id || i}
            href={e.link || e.htmlLink || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-1.5 px-1.5 rounded task-row-hover text-[12px]"
            data-testid={`meeting-${i}`}
          >
            <Calendar className="h-3 w-3 text-muted-foreground/30 shrink-0" />
            <span className="truncate flex-1">{e.title || e.summary || 'Meeting'}</span>
            <span className="text-[10px] text-muted-foreground/50">{formatTaskDate((e.start || e.start_time || '').slice(0, 10))}</span>
          </a>
        ))}
        {events.length === 0 && <EmptyState label="No upcoming meetings" />}
      </div>
      <WidgetFooter cardType="calendar_meetings" onClick={() => navigate('/tasks/calendar')} />
    </div>
  );
}

function TimesheetCard({ navigate, isAdmin, workspaceId }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  useEffect(() => {
    if (!workspaceId) return;
    dashboardCardAPI.cardData(workspaceId, 'timesheet', days).then(r => setData(r.data)).catch(() => {});
  }, [workspaceId, days]);
  if (!isAdmin) return <p className="text-[12px] text-muted-foreground/40 text-center py-4">Admin only</p>;
  if (!data) return <p className="text-[12px] text-muted-foreground/30 text-center py-4">Loading...</p>;
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-lg font-display font-bold">{data.total_hours}h</span>
        <div className="flex gap-1">
          {[7,14,30].map(d => (
            <button key={d} onClick={() => setDays(d)} className={`text-[9px] px-1.5 py-0.5 rounded ${days === d ? 'bg-gold/15 text-gold' : 'text-muted-foreground/30 hover:text-muted-foreground/50'}`}>{d}d</button>
          ))}
        </div>
      </div>
      <div className="flex-1 space-y-1 overflow-auto">
        {(data.members || []).slice(0, 6).map((m, i) => (
          <button
            key={i}
            onClick={() => m.user_id ? navigate(`/tasks/activity/${m.user_id}`) : navigate('/tasks/report')}
            className="flex items-center justify-between w-full py-1 px-1.5 rounded task-row-hover text-left"
            data-testid={`timesheet-member-${i}`}
          >
            <span className="text-[12px] truncate">{m.name}</span>
            <span className="text-[12px] font-medium tabular-nums">{m.hours}h</span>
          </button>
        ))}
      </div>
      <WidgetFooter cardType="timesheet" onClick={() => navigate('/tasks/report')} />
    </div>
  );
}

function DriveCard({ navigate, workspaceId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!workspaceId) return;
    dashboardCardAPI.cardData(workspaceId, 'drive_files').then(r => setData(r.data)).catch(() => {});
  }, [workspaceId]);
  if (!data) return <p className="text-[12px] text-muted-foreground/30 text-center py-4">Loading...</p>;
  if (!data.connected) return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex items-center justify-center">
        <EmptyState label="Google Drive not connected" cta="Connect now →" onCta={() => navigate('/tasks/integrations')} />
      </div>
    </div>
  );
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-0.5 overflow-auto">
        {(data.files || []).slice(0, 6).map(f => (
          <a key={f.id} href={f.webViewLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 py-1.5 px-1.5 rounded task-row-hover text-left w-full" data-testid={`drive-file-${f.id}`}>
            {f.iconLink && <img src={f.iconLink} alt="" className="h-4 w-4 shrink-0" />}
            <span className="text-[12px] truncate flex-1">{f.name}</span>
          </a>
        ))}
        {(data.files || []).length === 0 && <EmptyState label="No recent files" />}
      </div>
      <WidgetFooter cardType="drive_files" onClick={() => navigate('/tasks/integrations')} />
    </div>
  );
}


function TeamPulseCard({ workspaceId, isAdmin, navigate }) {
  const [pulse, setPulse] = useState([]);
  useEffect(() => {
    if (!workspaceId) return;
    // 2026-02-22 Bug 2b — Faster pulse refresh (10s instead of 30s) so
    // break/clock state changes propagate to the owner's dashboard in
    // near-real-time, plus an event-driven kick whenever the local
    // user's own clock state changes (own breaks immediately surface).
    const fetchPulse = () => dashboardCardAPI.cardData(workspaceId, 'team_pulse').then(r => setPulse(r.data || [])).catch(() => {});
    fetchPulse();
    const iv = setInterval(fetchPulse, 10000);
    const onClockChanged = () => fetchPulse();
    window.addEventListener('stellar-clock-changed', onClockChanged);
    window.addEventListener('focus', onClockChanged);
    return () => {
      clearInterval(iv);
      window.removeEventListener('stellar-clock-changed', onClockChanged);
      window.removeEventListener('focus', onClockChanged);
    };
  }, [workspaceId]);
  const statusDot = { active: 'bg-green-500', clocked_in: 'bg-green-500/60', on_break: 'bg-amber-500', clocked_out: 'bg-sky-500/60', online: 'bg-green-500/40', recent: 'bg-gold/50', away: 'bg-foreground/10' };
  const statusLabel = { active: 'Working', clocked_in: 'Clocked In', on_break: 'On break', clocked_out: 'Clocked out', online: 'Online', recent: 'Recent', away: 'Away' };
  // 2026-02 — Team Pulse now mirrors Attendance V2 (same canonical record
  // as the Attendance view + topbar widget). Subtitles show live durations.
  const fmtSince = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };
  const subtitleFor = (p) => {
    if (p.on_break) {
      const mins = p.break_started_at
        ? Math.max(0, Math.round((Date.now() - new Date(p.break_started_at).getTime()) / 60000))
        : null;
      return mins !== null ? `On break · ${mins}m` : 'On break';
    }
    const tasks = Array.isArray(p.in_progress_tasks) ? p.in_progress_tasks : [];
    if (tasks.length >= 1 && p.is_clocked_in) {
      if (tasks.length === 1) return `Working on: ${tasks[0].title}`;
      if (tasks.length === 2) return `Working on: ${tasks[0].title}, ${tasks[1].title}`;
      const extra = tasks.length - 2;
      return `Working on ${tasks.length} tasks: ${tasks[0].title}, ${tasks[1].title}, +${extra} more`;
    }
    if (p.active_task) return `Working on: ${p.active_task}`;
    if (p.is_clocked_in) {
      return p.clock_in_time ? `Clocked in since ${fmtSince(p.clock_in_time)}` : 'Clocked in — no active tasks';
    }
    if (p.is_clocked_out) return 'Clocked out for today';
    return p.is_online ? 'Online' : 'Not clocked in';
  };
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-1 overflow-auto">
        {pulse.slice(0, 10).map(p => {
          const Row = isAdmin ? 'button' : 'div';
          const rowProps = isAdmin
            ? {
                onClick: () => navigate(`/tasks/activity/${p.user_id}`),
                'data-testid': `team-pulse-${p.user_id}`,
                className: 'flex items-center gap-2 py-1.5 px-1.5 rounded task-row-hover w-full text-left',
              }
            : { className: 'flex items-center gap-2 py-1.5 px-1.5 rounded task-row-hover' };
          return (
            <Row key={p.user_id} {...rowProps}>
              <div className="relative">
                <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center shrink-0">
                  <span className="text-[8px] font-medium">{(p.name || '?')[0].toUpperCase()}</span>
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ${statusDot[p.status] || statusDot.away} ring-1 ring-card`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-[11px] font-medium truncate">
                    {p.name}
                    {p.role && <span className="text-muted-foreground/45 font-normal ml-1">({p.role === 'super_admin' || p.role === 'owner' ? 'Owner' : p.role === 'admin' ? 'Admin' : p.role === 'manager' ? 'Manager' : 'Member'})</span>}
                  </p>
                  <span className={`text-[8px] px-1 py-0 rounded ${p.status === 'active' || p.status === 'clocked_in' ? 'bg-green-500/10 text-green-400' : p.status === 'on_break' ? 'bg-amber-500/10 text-amber-400' : p.status === 'clocked_out' ? 'bg-sky-500/10 text-sky-400' : 'bg-foreground/[0.04] text-muted-foreground/30'}`}>{statusLabel[p.status] || 'Away'}</span>
                </div>
                <p
                  className="text-[9px] text-muted-foreground/35 truncate"
                  data-testid={`team-pulse-subtitle-${p.user_id}`}
                >
                  {subtitleFor(p)}
                </p>
              </div>
            </Row>
          );
        })}
        {pulse.length === 0 && <p className="text-[12px] text-muted-foreground/40 text-center py-4">No team data</p>}
      </div>
      {isAdmin && <WidgetFooter cardType="team_pulse" onClick={() => navigate('/tasks/team-view')} />}
    </div>
  );
}
